import { useResidentContext } from "app/features/residents/provider/ResidentProvider";
import { ModifyResidentRef } from "app/features/residents/templates/interfaces";
import { ResidentBioFormTemplate } from "app/features/residents/templates/modify/ResidentBioFormTemplate";
import { ResidentAllergiesFormTemplate } from "app/features/residents/templates/modify/ResidentAllergiesFormTemplate";
import { useRef, useState } from "react";
import { ScrollView, Text, View } from "react-native";
import { Button, ButtonText } from "ui/gluestack/button";
import { ResidentDietFormTemplate } from "app/features/residents/templates/modify/ResidentDietFormTemplate";
import { ResidentMedicalHistoryFormTemplate } from "app/features/residents/templates/modify/ResidentMedicalHistoryFormTemplate";
import { ResidentVaccinationsFormTemplate } from "app/features/residents/templates/modify/ResidentVaccinationsFormTemplate";
import { ResidentAdmissionFormTemplate } from "app/features/residents/templates/modify/ResidentAdmissionFormTemplate";
import { ResidentAssessmentFormTemplate } from "app/features/residents/templates/modify/ResidentAssessmentFormTemplate";
import { ResidentOtherInformationFormTemplate } from "app/features/residents/templates/modify/ResidentOtherInformationFormTemplate";
import { useRouter } from "expo-router";
import { ResidentPortfolioFormTemplate } from "app/features/residents/templates/modify/ResidentPortfolioFormTemplate";

const steps = [
  {
    title: "Resident Bio",
    component: (ref) => <ResidentBioFormTemplate ref={ref} />,
  },
  {
    title: "Allergies",
    component: (ref) => <ResidentAllergiesFormTemplate ref={ref} />,
    canSkip: true,
  },
  {
    title: "Diet Type & Allergies",
    component: (ref) => <ResidentDietFormTemplate ref={ref} />,
    canSkip: true,
  },
  {
    title: "Health Conditions and medical history",
    component: (ref) => <ResidentMedicalHistoryFormTemplate ref={ref} />,
    canSkip: true,
  },
  {
    title: "Vaccinations",
    component: (ref) => <ResidentVaccinationsFormTemplate ref={ref} />,
    canSkip: true,
  },
  {
    title: "Admission",
    component: (ref) => <ResidentAdmissionFormTemplate ref={ref} />,
    canSkip: true,
  },
  {
    title: "Assessments",
    component: (ref) => <ResidentAssessmentFormTemplate ref={ref} />,
    canSkip: true,
  },
  {
    title: "Resident Portfolio",
    component: (ref) => <ResidentPortfolioFormTemplate ref={ref} />,
    canSkip: true,
  },
  {
    title: "Other Information",
    component: (ref) => <ResidentOtherInformationFormTemplate ref={ref} />,
    canSkip: true,
  },
];

export default function AddResidentScreen() {
  const router = useRouter();
  const totalSteps = steps.length;
  const [currentStep, setCurrentStep] = useState(1);
  const ref = useRef<ModifyResidentRef>(null);
  const { resident } = useResidentContext();
  const handleNextStep = () => {
    ref.current
      ?.continue()
      .then((success) => {
        if (success) {
          if (currentStep === totalSteps) {
            router.replace(`/residents`);
          } else {
            setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
          }
        }
      })
      .catch(() => {});
  };

  const handlePreviousStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSkip = () => {
    if (currentStep === totalSteps) {
      router.replace(`/residents`);
    } else {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  return (
    <View className="bg-white flex-1">
      <ScrollView
        contentContainerClassName="px-dashboard"
        stickyHeaderIndices={[0]}
      >
        <View className="flex flex-row justify-between bg-white">
          <Text className="text-lg font-medium text-typography-800">
            {steps[currentStep - 1]?.title}
            {resident ? ` - ${resident.first_name} ${resident.last_name}` : ""}
          </Text>
          <Text className="text-xs font-normal text-typography-900 border-b border-primary-400 leading-4">
            Step {currentStep} of {totalSteps}
          </Text>
        </View>
        <View className="pt-16">
          {steps[currentStep - 1]?.component?.(ref)}
        </View>
        <View className="flex-row gap-4 justify-end mt-10">
          {steps[currentStep - 1]?.canSkip ? (
            <Button
              onPress={handleSkip}
              action="primary"
              variant="link"
              className="px-2"
            >
              <ButtonText>
                {currentStep === totalSteps ? "Skip & Complete" : "Skip"}
              </ButtonText>
            </Button>
          ) : null}
          {currentStep > 1 ? (
            <Button
              onPress={handlePreviousStep}
              action="primary"
              variant="outline"
            >
              <ButtonText>Previous</ButtonText>
            </Button>
          ) : null}
          <Button onPress={handleNextStep} action="primary">
            <ButtonText>
              {currentStep === totalSteps ? "Complete" : "Continue"}
            </ButtonText>
          </Button>
        </View>
      </ScrollView>
    </View>
  );
}
